// src/pages/WorkboardPage.jsx
//
// The folder replacement. Instead of remembering to check Saturday's folder
// for Monday's orders, this shows — unprompted, on screen, all day — exactly
// what has to be made right now and what's about to go out the door.
//
// It's a live subscription and it re-evaluates "today" every minute, so a
// screen left open at the counter overnight rolls over on its own.

import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { subscribeRecentOrders, setDesignStatus, setHandedOff, setNeededDate } from '../lib/db';
import {
  buildWorkboard, workflowOf, todayStr, relativeDay, relativePhrase, fmtDay, fmtTime, addDays,
} from '../lib/priority';

const TYPE_LABEL = {
  carry_out: 'Carry Out', local_delivery: 'Delivery',
  pick_up: 'Pick Up', wire_out: 'Wire Out', wire_in: 'Wire In',
};
const money = (n) => '$' + (n || 0).toFixed(2);

export function WorkboardPage({ setPage }) {
  const { shopId, shop, profile } = useAuth();
  const [orders,  setOrders]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [today,   setToday]   = useState(todayStr());
  const [busyId,  setBusyId]  = useState(null);

  // Live feed — no refresh button needed, and the back bench sees front-counter
  // orders the moment they're taken.
  useEffect(() => {
    if (!shopId) { setLoading(false); return; }
    const unsub = subscribeRecentOrders(shopId, {
      onData: (data) => { setOrders(data); setLoading(false); setError(''); },
      onError: () => { setError('Lost connection to the order feed. Check your internet.'); setLoading(false); },
    });
    return unsub;
  }, [shopId]);

  // Roll over at midnight without anyone touching the machine.
  useEffect(() => {
    const t = setInterval(() => setToday(todayStr()), 60_000);
    return () => clearInterval(t);
  }, []);

  const wf    = useMemo(() => workflowOf(shop), [shop]);
  const board = useMemo(() => buildWorkboard(orders, shop, today), [orders, shop, today]);

  const by = (u) => board.filter(o => o.sched.urgency === u);

  const late     = by('late');
  const makeToday= by('today');
  const flights  = by('flight');
  const startNow = by('start');
  const noDate   = by('nodate');
  const soon     = board.filter(o => ['soon', 'later'].includes(o.sched.urgency));

  // Reference lists (read-only — an order can appear here AND in a work list).
  const goingOutToday    = board.filter(o => o.sched.neededDate === today);
  const goingOutTomorrow = board.filter(o => o.sched.neededDate === addDays(today, 1));
  const madeToday        = board.filter(o => o.sched.urgency === 'done' && o.sched.neededDate >= today);

  const openWork = late.length + makeToday.length + flights.length + startNow.length;

  async function act(order, fn) {
    setBusyId(order.id);
    try { await fn(); }
    catch (e) { setError('Could not save: ' + e.message); }
    finally { setBusyId(null); }
  }

  const markDesign = (o, status) =>
    act(o, () => setDesignStatus(shopId, o.id, status, profile?.displayName));
  const markOut = (o) =>
    act(o, () => setHandedOff(shopId, o.id, true, profile?.displayName));
  const fixDate = (o, date) =>
    act(o, () => setNeededDate(shopId, o.id, date));

  const rowProps = { busyId, markDesign, markOut, today, setPage };

  if (loading) return <div className="loading-center"><div className="spinner"/></div>;

  const now = new Date();

  return (
    <div className="workboard">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="wb-header">
        <div>
          <h1 style={{ color: 'var(--g800)' }}>Workboard</h1>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 3 }}>
            {now.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' })}
            {' · '}
            {openWork === 0
              ? 'Nothing outstanding — you\'re clear.'
              : `${openWork} ${openWork === 1 ? 'order needs' : 'orders need'} attention`}
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setPage('new-order')}>➕ New Order</button>
      </div>

      {error && <div className="auth-error" style={{ marginBottom: 14 }}>{error}</div>}

      {/* ── Counters ───────────────────────────────────────────────────────── */}
      <div className="wb-counters">
        <Counter tone="red"   n={late.length}      label="Behind"        sub="should be made already"/>
        <Counter tone="red"   n={makeToday.length} label="Make today"    sub="needed tomorrow"/>
        <Counter tone="amber" n={flights.length}   label="Flights"       sub="out of town"/>
        <Counter tone="amber" n={startNow.length}  label="Start now"     sub="big / early orders"/>
        <Counter tone="blue"  n={goingOutToday.length} label="Out today" sub="pickups & deliveries"/>
      </div>

      {/* ── Work sections ──────────────────────────────────────────────────── */}
      {openWork === 0 && noDate.length === 0 && (
        <div className="wb-clear">
          <div style={{ fontSize: 34 }}>✓</div>
          <h3>All caught up</h3>
          <p>Every order due in the next few days is made or already handled.</p>
        </div>
      )}

      <Section
        show={late.length > 0}
        tone="red" icon="⚠️" title="Behind — make these first"
        blurb="These should already have been finished. Anything needed today lands here too."
        orders={late} {...rowProps}
      />

      <Section
        show={makeToday.length > 0}
        tone="red" icon="🌸" title="Make today"
        blurb="Needed tomorrow. Finish these before you close so nobody's waiting at the counter at 10am."
        orders={makeToday} {...rowProps}
      />

      <Section
        show={flights.length > 0}
        tone="amber" icon="✈️" title="Out of town — flights"
        blurb={`Confirm the flight, then have it at the airport ${wf.airportCutoffMins} minutes before departure.`}
        orders={flights} {...rowProps}
      />

      <Section
        show={startNow.length > 0}
        tone="amber" icon="⏳" title="Start now — big orders"
        blurb={`$${wf.bigOrderThreshold}+ orders get ${wf.bigOrderLeadDays} open days; $${wf.hugeOrderThreshold}+ get ${wf.hugeOrderLeadDays}. Begin these now so the busy day doesn't eat them.`}
        orders={startNow} {...rowProps}
      />

      {/* ── Orders with no date — the legacy gap, surfaced not hidden ──────── */}
      {noDate.length > 0 && (
        <div className="wb-section tone-gray">
          <div className="wb-section-head">
            <span className="wb-section-title">❓ No date set — {noDate.length}</span>
          </div>
          <p className="wb-blurb">
            These were taken without a needed-by date, so the board can't schedule them.
            Set a date and they'll slot into the right day.
          </p>
          {noDate.map(o => (
            <div key={o.id} className="wb-row">
              <div className="wb-row-main">
                <OrderIdentity o={o}/>
              </div>
              <div className="wb-row-actions">
                <input
                  className="input sm" type="date" style={{ width: 175 }}
                  defaultValue={today}
                  onChange={(e) => e.target.value && fixDate(o, e.target.value)}
                />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Counter reference: what physically leaves today & tomorrow ─────── */}
      <div className="wb-two-col">
        <HandoffList
          title="Going out today" icon="📤" orders={goingOutToday}
          empty="Nothing scheduled to leave today." onOut={markOut} busyId={busyId}
        />
        <HandoffList
          title="Going out tomorrow" icon="📅" orders={goingOutTomorrow}
          empty="Nothing scheduled for tomorrow yet." onOut={markOut} busyId={busyId}
        />
      </div>

      {/* ── The week ahead ─────────────────────────────────────────────────── */}
      <WeekAhead board={board} today={today} wf={wf}/>

      {/* ── Finished ───────────────────────────────────────────────────────── */}
      {madeToday.length > 0 && (
        <details className="card wb-done">
          <summary>✅ Made &amp; ready — {madeToday.length}</summary>
          <div style={{ marginTop: 10 }}>
            {madeToday.map(o => (
              <div key={o.id} className="wb-row done">
                <div className="wb-row-main"><OrderIdentity o={o}/></div>
                <div className="wb-row-actions">
                  <span className="wb-when">{relativeDay(o.sched.neededDate, today)}</span>
                  {!o.sched.handedOff && (
                    <button className="btn btn-secondary btn-sm" disabled={busyId === o.id}
                      onClick={() => markOut(o)}>Handed off</button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// ─── Pieces ───────────────────────────────────────────────────────────────────

function Counter({ tone, n, label, sub }) {
  return (
    <div className={`wb-counter tone-${tone} ${n === 0 ? 'is-zero' : ''}`}>
      <div className="wb-counter-n">{n}</div>
      <div className="wb-counter-label">{label}</div>
      <div className="wb-counter-sub">{sub}</div>
    </div>
  );
}

function Section({ show, tone, icon, title, blurb, orders, ...rowProps }) {
  if (!show) return null;
  return (
    <div className={`wb-section tone-${tone}`}>
      <div className="wb-section-head">
        <span className="wb-section-title">{icon} {title} — {orders.length}</span>
      </div>
      {blurb && <p className="wb-blurb">{blurb}</p>}
      {orders.map(o => <WorkRow key={o.id} o={o} {...rowProps}/>)}
    </div>
  );
}

function WorkRow({ o, busyId, markDesign, markOut, today }) {
  const s = o.sched;
  const busy = busyId === o.id;

  return (
    <div className={`wb-row tone-${s.tone}`}>
      <div className="wb-row-main">
        <OrderIdentity o={o}/>
        <div className="wb-chips">
          {s.reasons.map(r => (
            <span key={r.key} className={`wb-chip chip-${r.tone}`}>{r.label}</span>
          ))}
          {s.design === 'in_progress' && <span className="wb-chip chip-blue">In progress</span>}
        </div>
        {s.outOfTown && <FlightLine s={s} today={today}/>}
        {o.delivery?.instructions && (
          <div className="wb-note">📝 {o.delivery.instructions}</div>
        )}
      </div>

      <div className="wb-row-when">
        <div className="wb-when-primary">{relativeDay(s.neededDate, today)}</div>
        <div className="wb-when-sub">
          needed {fmtDay(s.neededDate)}{s.neededTime ? ` · ${s.neededTime}` : ''}
        </div>
        {s.finishBy && s.finishBy !== s.neededDate && (
          <div className="wb-when-sub">finish by {fmtDay(s.finishBy)}</div>
        )}
      </div>

      <div className="wb-row-actions">
        {s.design !== 'in_progress' && (
          <button className="btn btn-secondary btn-sm" disabled={busy}
            onClick={() => markDesign(o, 'in_progress')}>Start</button>
        )}
        <button className="btn btn-primary btn-sm" disabled={busy}
          onClick={() => markDesign(o, 'done')}>{busy ? '…' : 'Made ✓'}</button>
      </div>
    </div>
  );
}

/** Who this arrangement is for. Recipient wins; falls back to the buyer. */
function whoFor(o) {
  const r = `${o.recipient?.firstName || ''} ${o.recipient?.lastName || ''}`.trim();
  return r || o.customerName || '—';
}

function OrderIdentity({ o }) {
  const who = whoFor(o);
  const items = (o.items || []).map(i => `${i.qty}× ${i.description}`).join(', ');
  return (
    <div>
      <div className="wb-row-title">
        <span className="wb-num">#{o.orderNumber || '—'}</span>
        <span className="wb-who">{who}</span>
        <span className="pill pill-gray" style={{ fontSize: 10.5 }}>{TYPE_LABEL[o.type] || o.type}</span>
        <span className="wb-total">{money(o.total)}</span>
      </div>
      {items && <div className="wb-items">{items}</div>}
    </div>
  );
}

function FlightLine({ s, today }) {
  const isToday = s.flightDate === today;
  const when = relativePhrase(s.flightDate, today);
  return (
    <div className={`wb-flight ${isToday ? 'urgent' : ''}`}>
      ✈️ {s.flight?.airline || 'Flight'} {s.flight?.number || ''}
      {s.flight?.departTime && <> departs <strong>{fmtTime(s.flight.departTime)}</strong></>}
      {' '}{when || `on ${fmtDay(s.flightDate)}`}
      {s.dropOffBy && <> — at the airport by <strong>{fmtTime(s.dropOffBy)}</strong></>}
    </div>
  );
}

function HandoffList({ title, icon, orders, empty, onOut, busyId }) {
  return (
    <div className="card">
      <div className="card-header">
        <span className="card-title">{icon} {title}</span>
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{orders.length}</span>
      </div>
      {orders.length === 0
        ? <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{empty}</p>
        : orders.map(o => (
            <div key={o.id} className="wb-handoff">
              <span className={`wb-dot ${o.sched.design === 'done' ? 'ok' : 'pending'}`}
                title={o.sched.design === 'done' ? 'Made' : 'Not made yet'}/>
              <span className="wb-num">#{o.orderNumber}</span>
              <span className="wb-handoff-who">{whoFor(o)}</span>
              <span className="wb-handoff-meta">
                {TYPE_LABEL[o.type]}{o.sched.neededTime ? ` · ${o.sched.neededTime}` : ''}
              </span>
              {o.sched.design === 'done' && !o.sched.handedOff && (
                <button className="btn btn-ghost btn-sm" disabled={busyId === o.id}
                  onClick={() => onOut(o)}>Out ✓</button>
              )}
            </div>
          ))}
    </div>
  );
}

/** Seven-day strip so nothing sneaks up — the folder drawer, at a glance. */
function WeekAhead({ board, today, wf }) {
  const days = Array.from({ length: 7 }, (_, i) => addDays(today, i));
  const closed = (d) => (wf.closedDays || []).includes(new Date(d + 'T12:00:00').getDay());

  return (
    <div className="card">
      <div className="card-header"><span className="card-title">📆 Next 7 days</span></div>
      <div className="wb-week">
        {days.map(d => {
          const forDay  = board.filter(o => o.sched.neededDate === d);
          const toMake  = board.filter(o => o.sched.finishBy === d && o.sched.design !== 'done');
          const value   = forDay.reduce((s, o) => s + (o.total || 0), 0);
          return (
            <div key={d} className={`wb-day ${d === today ? 'is-today' : ''} ${closed(d) ? 'is-closed' : ''}`}>
              <div className="wb-day-name">{fmtDay(d).split(',')[0]}</div>
              <div className="wb-day-date">{fmtDay(d).split(', ')[1]}</div>
              {closed(d) && <div className="wb-day-closed">Closed</div>}
              <div className="wb-day-n">{forDay.length}</div>
              <div className="wb-day-sub">out</div>
              {toMake.length > 0 && <div className="wb-day-make">{toMake.length} to make</div>}
              {value > 0 && <div className="wb-day-val">{money(value)}</div>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
